/**
 * ComponentWrapper - Unified Component Wrapper System
 *
 * Consolidates all wrapper functionality into a single, efficient component:
 * - Positioning (absolute, responsive, flex)
 * - Selection and hover states
 * - Resize functionality
 * - Mode-based rendering (editing/viewing/functional)
 * - Event handling
 * - Performance optimization
 *
 * Replaces: UnifiedComponentWrapper, SelectableWrapper, ResponsiveComponentWrapper,
 * EnhancedResizableComponent, and mode-specific logic from other wrappers
 */

import React, { useCallback, useEffect, useMemo, useRef, CSSProperties, ReactNode } from "react";
import { Component, Position, Viewport, ComponentType } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import { InteractionMode } from "./InteractionContext";
import { useInteractionContext } from "./InteractionContextRuntime";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useViewerStore, useViewerComponentMap } from "@/stores/viewer/viewer-store";
import { useResponsiveStore, ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
// [Resize Removal] ResizableComponent import removed - AI-first architecture
// Selection/hover functionality is now handled directly in this component
// All component sizing is done via AI chat commands
import { SharedComponentRenderer } from "./SharedComponentRenderer";
import { responsivePositionTranslator } from "@/services/responsive/ResponsivePositionTranslator";
// responsive-position-calculator imports removed - unused (flex-only architecture)
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { getComponentDepth } from "@/utils/componentHierarchy";
import { getColorForDepth, HOVER_COLOR, LOCKED_COLOR, shouldShowDepthWarning } from "@/utils/selectionColors";
import { getBreakpointFromViewport } from "@/utils/breakpoint-utils";
import { withNormalizedComponentStyle } from "@/utils/component-style-normalization";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipPortal,
} from "@/components/ui/tooltip";
import type { TooltipConfig } from "@/types";
import type { InteractionTriggerType } from "@/types/interactions";
import { interactionRuntime, type InteractionContext as IxnContext } from "@/services/editor/InteractionRuntime";
import { useComponentDataStore } from "@/stores/editor/component-data-store";
import { liveInputValues } from "@/services/editor/live-input-values";

/**
 * Components with native tooltip systems (showTooltip: boolean).
 * These render their own data visualization tooltips and should NOT
 * use the generic TooltipConfig wrapper.
 */
const NATIVE_TOOLTIP_COMPONENTS = new Set([
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.AREA_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.MAP_CHART,
]);

const VIEWER_FULL_WIDTH_COMPONENTS = new Set<ComponentType | string>([
  ComponentType.CONTAINER,
  ComponentType.FLEX_CONTAINER,
  ComponentType.CARD,
  ComponentType.FORM_CONTAINER,
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.AREA_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.MAP_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.IMAGE,
  ComponentType.FILE_STORAGE,
  ComponentType.ASSIGNMENT_WIDGET,
  ComponentType.HORIZONTAL_TIMELINE,
  ComponentType.CHECKLIST,
]);

const isFullWidthViewerComponent = (component: Component): boolean => {
  return VIEWER_FULL_WIDTH_COMPONENTS.has(component.type);
};

const isFixedWidthValue = (width: CSSProperties['width']): boolean => {
  if (typeof width === 'number') return true;
  if (typeof width !== 'string') return false;

  const trimmedWidth = width.trim();
  if (trimmedWidth.length === 0) return false;
  if (trimmedWidth.endsWith('%')) return parseFloat(trimmedWidth) < 100;

  return /^-?\d+(\.\d+)?(px|rem|em|vw|vh)$/.test(trimmedWidth) || /^-?\d+(\.\d+)?$/.test(trimmedWidth);
};

const hasFixedViewerWidthConstraint = (styles: CSSProperties): boolean => {
  return isFixedWidthValue(styles.width) || isFixedWidthValue(styles.maxWidth);
};

const isChartOrTableComponent = (component: Component): boolean => {
  return [
    ComponentType.CHART,
    ComponentType.BAR_CHART,
    ComponentType.LINE_CHART,
    ComponentType.PIE_CHART,
    ComponentType.SCATTER_CHART,
    ComponentType.AREA_CHART,
    ComponentType.HEATMAP_CHART,
    ComponentType.TREEMAP_CHART,
    ComponentType.GAUGE_CHART,
    ComponentType.VERTICAL_GAUGE_CHART,
    ComponentType.TABLE,
    ComponentType.ADVANCED_TABLE,
  ].includes(component.type);
};

const shouldUseFullViewerWidth = (
  component: Component,
  mode: InteractionMode,
  isFlexChild?: boolean
): boolean => {
  if (isFlexChild || (mode !== 'viewing' && mode !== 'functional')) return false;

  const isRootComponent = !component.parentId || component.parentId === 'root';
  return isChartOrTableComponent(component) || (isRootComponent && isFullWidthViewerComponent(component));
};

const getNumericPositionValue = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const isCssDimensionValue = (value: unknown): value is string | number =>
  typeof value === 'string' || typeof value === 'number';

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface ComponentWrapperProps {
  component: Component;
  children?: ReactNode;

  // Canvas context
  viewport?: Viewport | ResponsiveBreakpoint;
  containerDimensions?: { width: number; height: number };
  canvasWidth?: number;
  canvasHeight?: number;

  // Parent context (for flex children)
  isFlexChild?: boolean;
  flexDirection?: "row" | "column" | "row-reverse" | "column-reverse";

  // Performance hints
  enableVirtualization?: boolean;
  viewportBounds?: { x: number; y: number; width: number; height: number };
  /** @deprecated Spatial indexing has been removed */
  spatialIndex?: unknown;

  // Custom styling
  className?: string;
  style?: CSSProperties;
  activeColorScheme?: ColorScheme;

  // Event handlers (for parent components)
  onComponentInteraction?: (type: string, data: unknown) => void;
}

// ============================================================================
// Helper Hooks
// ============================================================================

/**
 * Unified position calculation hook
 * Handles absolute, responsive, and flex positioning in one place
 */
const useUnifiedPosition = (
  component: Component,
  mode: InteractionMode,
  viewport?: Viewport | ResponsiveBreakpoint,
  containerDimensions?: { width: number; height: number },
  isFlexChild?: boolean
) => {
  const { getResponsiveProperty } = useResponsiveStore();

  return useMemo(() => {
    // For flex children, DON'T apply flex constraints here
    // FlexContainer already computes and applies the correct flex constraints
    // with gap compensation (e.g., "calc(50% - 6px)" → "45%") on its wrapper div.
    // Applying flex constraints here would be redundant and could cause conflicts.
    if (isFlexChild) {
      const baseStyles: CSSProperties = {
        position: "relative" as const,
        width: undefined,
        height: undefined,
        left: undefined,
        top: undefined,
        zIndex: component.zIndex ?? 1,
        // Note: flexBasis, flexGrow, flexShrink, alignSelf are NOT applied here
        // They are handled by FlexContainer's wrapper div with proper gap compensation
      };

      return baseStyles;
    }

    // In viewer/functional mode, use responsive positioning
    if ((mode === "viewing" || mode === "functional") && viewport && containerDimensions) {
      // Get responsive values from store or component
      const viewportId = typeof viewport === 'string' ? viewport : viewport.id;
      const responsiveX = getResponsiveProperty(component.id, "position.x", viewportId);
      const responsiveY = getResponsiveProperty(component.id, "position.y", viewportId);

      const basePosition: Position = {
        x: getNumericPositionValue(responsiveX) ?? 0,
        y: getNumericPositionValue(responsiveY) ?? 0,
        z: component.zIndex ?? 1,
      };

      // Convert viewport to Viewport enum for the translator using width-based detection
      const breakpointName = getBreakpointFromViewport(viewport);
      const viewportEnum: Viewport = breakpointName === 'mobile' ? Viewport.MOBILE :
                                     breakpointName === 'tablet' ? Viewport.TABLET :
                                     Viewport.DESKTOP;

      // Use responsive position translator for viewer mode
      let responsiveStyles: CSSProperties = responsivePositionTranslator.translateToResponsive(
        { ...component, position: basePosition },
        {
          viewport: viewportEnum,
          containerDimensions,
          enableSnapping: false
        }
      );

      // Filter out minHeight for FlexContainers - let them size based on children
      if (component.type === 'flex-container') {
        const { minHeight: _minHeight, ...stylesWithoutMinHeight } = responsiveStyles;
        responsiveStyles = stylesWithoutMinHeight;
      }

      // Apply responsive breakpoint styles if defined
      if (component.responsivePosition?.breakpoints) {
        // Reuse breakpointName already computed with width-based detection
        const breakpointData = component.responsivePosition.breakpoints[breakpointName];
        const breakpointStyle = breakpointData?.style;
        const breakpointSize = breakpointData?.size;

        if (breakpointStyle || breakpointSize) {
          // For root containers in viewer/functional mode, override width to be 100%
          // This ensures templates use full available width in viewer
          const isRootContainer = !component.parentId || component.parentId === 'root';
          const isContainer = component.type === 'flex-container' || component.type === 'container' || component.type === 'card';

          // Chart and table components should also get full width treatment in viewer
          const isChart = ['chart', 'bar-chart', 'line-chart', 'pie-chart', 'scatter-chart',
                          'area-chart', 'heatmap-chart', 'treemap-chart', 'gauge-chart', 'vertical-gauge-chart'].includes(component.type);
          const isTable = ['table', 'advanced-table'].includes(component.type);

          // Determine width: prioritize responsiveSize.width, fall back to breakpointStyle.width
          const breakpointWidth = breakpointSize?.width ?? breakpointStyle?.width;

          // Apply width from breakpoint (override to 100% for root containers, charts, and tables in viewer)
          if (isCssDimensionValue(breakpointWidth)) {
            if ((mode === 'viewing' || mode === 'functional') &&
                ((isRootContainer && isContainer) || isChart || isTable)) {
              responsiveStyles.width = '100%';
              responsiveStyles.maxWidth = 'none';
            } else {
              responsiveStyles.width = breakpointWidth;
            }
          }
          // Apply height from breakpoint (prioritize responsiveSize.height)
          const breakpointHeight = breakpointSize?.height ?? breakpointStyle?.height;
          if (isCssDimensionValue(breakpointHeight)) {
            // Enforce minimum height for charts even when explicitly set smaller
            if (isChart && typeof breakpointHeight === 'number') {
              const getChartMinimum = (chartType: string): number => {
                if (chartType === 'line-chart' || chartType === 'area-chart') return 100;
                if (chartType === 'pie-chart' || chartType === 'gauge-chart' || chartType === 'heatmap-chart') return 120;
                if (chartType === 'treemap-chart') return 150;
                if (chartType === 'bar-chart' || chartType === 'scatter-chart' || chartType === 'chart') return 100;
                return 100;
              };
              const chartMinimum = getChartMinimum(component.type);
              const finalHeight = Math.max(breakpointHeight, chartMinimum);

              responsiveStyles.height = finalHeight;
            } else {
              responsiveStyles.height = breakpointHeight;
            }
          } else if (isChart || isTable) {
            // Ensure charts and tables have minimum height even when breakpoint height isn't set
            responsiveStyles.height = responsiveStyles.height || 300;
          }

          // Apply constraints from breakpoint (skip maxWidth for root containers, charts, and tables in viewer)
          const breakpointMinWidth = breakpointSize?.minWidth ?? breakpointStyle?.minWidth;
          if (isCssDimensionValue(breakpointMinWidth)) {
            if (!((mode === 'viewing' || mode === 'functional') &&
                  ((isRootContainer && isContainer) || isChart || isTable))) {
              responsiveStyles.minWidth = breakpointMinWidth;
            }
          }

          const breakpointMaxWidth = breakpointSize?.maxWidth ?? breakpointStyle?.maxWidth;
          if (isCssDimensionValue(breakpointMaxWidth)) {
            if (!((mode === 'viewing' || mode === 'functional') &&
                  ((isRootContainer && isContainer) || isChart || isTable))) {
              responsiveStyles.maxWidth = breakpointMaxWidth;
            }
          }

          const breakpointMinHeight = breakpointSize?.minHeight ?? breakpointStyle?.minHeight;
          // Don't apply minHeight to FlexContainers - let them size based on children
          const isFlexContainer = component.type === 'flex-container';
          if (isCssDimensionValue(breakpointMinHeight) && !isFlexContainer) {
            responsiveStyles.minHeight = breakpointMinHeight;
          } else if (isChart) {
            // Set chart-specific minimum heights if not already set
            const getChartMinimum = (chartType: string): number => {
              if (chartType === 'line-chart' || chartType === 'area-chart') return 100;
              if (chartType === 'pie-chart' || chartType === 'gauge-chart' || chartType === 'heatmap-chart') return 120;
              if (chartType === 'treemap-chart') return 150;
              if (chartType === 'bar-chart' || chartType === 'scatter-chart' || chartType === 'chart') return 100;
              return 100;
            };
            const chartMinHeight = getChartMinimum(component.type);
            responsiveStyles.minHeight = chartMinHeight;
          }

          const breakpointMaxHeight = breakpointSize?.maxHeight ?? breakpointStyle?.maxHeight;
          if (isCssDimensionValue(breakpointMaxHeight)) {
            responsiveStyles.maxHeight = breakpointMaxHeight;
          }
          // Note: padding is handled by component.style, not by TranslatedStyles
        }
      }

      // Override calculated width if component.style.width is already set to a percentage or explicit value
      if (component.style?.width && typeof component.style.width === 'string') {
        responsiveStyles.width = component.style.width;
      }

      // Final failsafe: Ensure full-width viewer surfaces fill the measured viewer host.
      // This catches cases where breakpoint styles are not defined or arrive as fixed editor widths.
      if ((mode === 'viewing' || mode === 'functional')) {
        const isChart = ['chart', 'bar-chart', 'line-chart', 'pie-chart', 'scatter-chart',
                        'area-chart', 'heatmap-chart', 'treemap-chart', 'gauge-chart', 'vertical-gauge-chart'].includes(component.type);
        const isTable = ['table', 'advanced-table'].includes(component.type);
        const isRootContainer = !component.parentId || component.parentId === 'root';
        const isContainer = component.type === 'flex-container' || component.type === 'container' || component.type === 'card';
        const shouldUseFullViewerWidthForPosition =
          isChart ||
          isTable ||
          (isRootContainer && (isContainer || isFullWidthViewerComponent(component)));

        if (shouldUseFullViewerWidthForPosition) {
          if (hasFixedViewerWidthConstraint(responsiveStyles)) {
            responsiveStyles.width = '100%';
            responsiveStyles.maxWidth = 'none';
          }
        }
      }

      return responsiveStyles;
    }

    // Fallback: Use flex/relative positioning (absolute positioning removed)
    // TerraMatrix is now AI-chat-only with flex-based layouts
    // All components should be flex children, but provide safe fallback for edge cases
    return {
      position: "relative" as const,
      width: component.size?.width ?? '100%',
      height: component.size?.height ?? 'auto',
      zIndex: component.zIndex ?? 1,
    };
  }, [component, mode, viewport, containerDimensions, isFlexChild, getResponsiveProperty]);
};

/**
 * Normalize CSS shorthand/longhand conflicts to prevent React warnings.
 * When both a shorthand (e.g. `margin`) and any of its longhands (e.g. `marginBottom`)
 * are present, expand the shorthand into longhands (without overwriting existing longhands)
 * and omit the shorthand.
 */
const normalizeShorthandConflicts = (styles: CSSProperties): CSSProperties => {
  const expandBoxShorthand = (value: unknown): [string, string, string, string] => {
    const parts = String(value).trim().split(/\s+/);

    if (parts.length === 1) {
      return [parts[0], parts[0], parts[0], parts[0]];
    }

    if (parts.length === 2) {
      return [parts[0], parts[1], parts[0], parts[1]];
    }

    if (parts.length === 3) {
      return [parts[0], parts[1], parts[2], parts[1]];
    }

    return [parts[0], parts[1], parts[2], parts[3]];
  };

  let normalizedStyles = styles;

  if (
    normalizedStyles.margin !== undefined &&
    (
      normalizedStyles.marginTop !== undefined ||
      normalizedStyles.marginRight !== undefined ||
      normalizedStyles.marginBottom !== undefined ||
      normalizedStyles.marginLeft !== undefined
    )
  ) {
    const [top, right, bottom, left] = expandBoxShorthand(normalizedStyles.margin);
    const { margin: _margin, ...rest } = normalizedStyles;
    normalizedStyles = {
      ...rest,
      marginTop: rest.marginTop ?? top,
      marginRight: rest.marginRight ?? right,
      marginBottom: rest.marginBottom ?? bottom,
      marginLeft: rest.marginLeft ?? left,
    };
  }

  if (
    normalizedStyles.padding !== undefined &&
    (
      normalizedStyles.paddingTop !== undefined ||
      normalizedStyles.paddingRight !== undefined ||
      normalizedStyles.paddingBottom !== undefined ||
      normalizedStyles.paddingLeft !== undefined
    )
  ) {
    const [top, right, bottom, left] = expandBoxShorthand(normalizedStyles.padding);
    const { padding: _padding, ...rest } = normalizedStyles;
    normalizedStyles = {
      ...rest,
      paddingTop: rest.paddingTop ?? top,
      paddingRight: rest.paddingRight ?? right,
      paddingBottom: rest.paddingBottom ?? bottom,
      paddingLeft: rest.paddingLeft ?? left,
    };
  }

  return normalizedStyles;
};

const isContentDrivenHeightValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'auto'
    || normalized.startsWith('fit-content')
    || normalized.startsWith('max-content')
    || normalized.startsWith('min-content');
};

const hasLegacyDefaultTableMaxHeight = (component: Component): boolean => {
  if (component.type !== ComponentType.TABLE && component.type !== ComponentType.ADVANCED_TABLE) {
    return false;
  }

  const properties = component.properties as Record<string, unknown> | undefined;
  return properties?.maxHeight === 400
    && properties?.enablePagination !== false
    && properties?.dashboardMode !== true;
};

const hasLegacyDefaultTableFixedHeight = (component: Component): boolean => {
  if (component.type !== ComponentType.TABLE && component.type !== ComponentType.ADVANCED_TABLE) {
    return false;
  }

  const properties = component.properties as Record<string, unknown> | undefined;
  const style = component.style as CSSProperties | undefined;
  const sizeHeight = component.size?.height;

  return properties?.enablePagination !== false
    && properties?.dashboardMode !== true
    && sizeHeight === 400
    && (style?.height === undefined || style.height === 400 || style.height === '400px')
    && (properties?.height === undefined || properties.height === 400 || properties.height === '400px');
};

const componentUsesContentDrivenHeight = (component: Component): boolean => {
  const propertyStyle = component.properties?.style as Record<string, unknown> | undefined;
  const isAutoHeightTable =
    (component.type === ComponentType.TABLE || component.type === ComponentType.ADVANCED_TABLE) &&
    (component.properties?.maxHeight === undefined || hasLegacyDefaultTableMaxHeight(component)) &&
    component.properties?.enablePagination !== false;
  return isContentDrivenHeightValue(component.style?.height)
    || isContentDrivenHeightValue(component.style?.minHeight)
    || isContentDrivenHeightValue(component.properties?.height)
    || isContentDrivenHeightValue(component.properties?.minHeight)
    || isContentDrivenHeightValue(propertyStyle?.height)
    || isContentDrivenHeightValue(propertyStyle?.minHeight)
    || hasLegacyDefaultTableFixedHeight(component)
    || isAutoHeightTable;
};

const isAutoWidthValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'auto'
    || normalized.startsWith('fit-content')
    || normalized.startsWith('max-content')
    || normalized.startsWith('min-content');
};

const isRecordValue = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getResponsiveConstraintValue = (
  constraints: unknown,
  key: 'alignSelf' | 'flexBasis',
  breakpointName?: string
): unknown => {
  if (!isRecordValue(constraints)) return undefined;

  const directValue = constraints[key];
  if (directValue !== undefined) return directValue;

  const breakpointConstraints = breakpointName ? constraints[breakpointName] : undefined;
  if (isRecordValue(breakpointConstraints)) {
    const breakpointValue = breakpointConstraints[key];
    if (breakpointValue !== undefined) return breakpointValue;
  }

  const baseConstraints = constraints.base;
  return isRecordValue(baseConstraints) ? baseConstraints[key] : undefined;
};

const parsePixelDimension = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px)?$/i);
  return match ? Number(match[1]) : 0;
};

const isToolbarLikeContainer = (component: Component): boolean => {
  const semantic = `${component.id || ''} ${component.name || ''}`.toLowerCase();
  return /trigger|top controls|top-control|top bar|topbar|nav|footer|breadcrumb|banner/.test(semantic);
};

const shouldIgnoreSmallFixedHeightOverride = (component: Component): boolean => {
  const isFlexContainer = component.type === ComponentType.FLEX_CONTAINER ||
    component.type === ComponentType.CONTAINER ||
    (component.type as string) === 'flex-container';
  if (!isFlexContainer || !component.parentId || isToolbarLikeContainer(component)) return false;

  const properties = component.properties as Record<string, unknown> | undefined;
  if ((properties?.modal as Record<string, unknown> | undefined)?.enabled) return false;

  const styleHeight = parsePixelDimension(component.style?.height ?? properties?.height);
  const sizeHeight = parsePixelDimension(component.size?.height);

  return styleHeight > 0 && styleHeight <= 80 && sizeHeight > styleHeight + 24;
};

/**
 * Unified style calculation hook
 * Merges base styles, mode styles, and component-specific styles
 */
const useUnifiedStyles = (
  component: Component,
  positionStyles: CSSProperties,
  mode: InteractionMode,
  isSelected: boolean,
  className?: string,
  customStyle?: CSSProperties,
  isFlexChild?: boolean
): CSSProperties => {
  return useMemo(() => {
    // For flex children, strip WIDTH-related sizing properties from component.style
    // FlexContainer's child wrapper controls width via flexBasis
    // BUT keep HEIGHT properties - charts need explicit height to render (they use height: 100% internally)
    // The "phantom full-width container" bug was about width, not height
    let componentStyle = component.style;
    if (isFlexChild && componentStyle) {
      const stylesWithHeight = { ...(componentStyle as CSSProperties) };
      delete stylesWithHeight.width;
      delete stylesWithHeight.minWidth;
      delete stylesWithHeight.maxWidth;
      componentStyle = stylesWithHeight;
      if (shouldIgnoreSmallFixedHeightOverride(component) || hasLegacyDefaultTableFixedHeight(component)) {
        const stylesWithoutStaleHeight = { ...(componentStyle as CSSProperties) };
        delete stylesWithoutStaleHeight.height;
        delete stylesWithoutStaleHeight.minHeight;
        delete stylesWithoutStaleHeight.maxHeight;
        componentStyle = stylesWithoutStaleHeight;
      }
    }

    let styles: CSSProperties = {
      ...positionStyles,
      ...componentStyle,
      ...customStyle,

      // Transition for smooth animations
      transition: "all 200ms ease-out",

      // Handle visibility states
      opacity: component.hidden ? 0.3 : 1,
      pointerEvents: component.hidden || component.locked ? "none" : "auto",

      // Cursor based on mode and state
      cursor: (() => {
        if (component.locked || component.hidden) return "default";
        if (mode === "editing") {
          if (isSelected) return "move";
          return "pointer";
        }
        if (mode === "viewing") {
          // In viewing mode (AI-first workflow), show pointer for selection
          return "pointer";
        }
        if (mode === "functional") {
          if (component.type === ComponentType.BUTTON) return "pointer";
          if (component.type === ComponentType.INPUT) return "text";
          if (component.properties?.action === 'navigate' && component.properties?.target) return "pointer";
          if (component.interactions?.length) return "pointer";
          // Check if any ancestor has navigation (pointer for children of navigable containers)
          if (component.parentId) {
            const vMap = useViewerStore.getState().componentMap;
            const eComps = useEditorStore.getState().components;
            const lookup = (id: string): Component | undefined => {
              if (vMap?.size) return vMap.get(id);
              return eComps.find((c: Component) => c.id === id);
            };
            let pid: string | undefined = component.parentId;
            while (pid) {
              const p = lookup(pid);
              if (!p) break;
              if (p.properties?.action === 'navigate' && p.properties?.target) return "pointer";
              pid = p.parentId;
            }
          }
        }
        return "default";
      })(),
    };

    if (shouldUseFullViewerWidth(component, mode, isFlexChild) && hasFixedViewerWidthConstraint(styles)) {
      styles.width = '100%';
      styles.maxWidth = 'none';
    }

    // Remove minWidth from charts/tables in viewer/functional mode to allow responsive behavior
    if (mode === 'viewing' || mode === 'functional') {
      const isChart = ['chart', 'bar-chart', 'line-chart', 'pie-chart', 'scatter-chart',
                      'area-chart', 'heatmap-chart', 'treemap-chart', 'gauge-chart', 'vertical-gauge-chart'].includes(component.type);
      const isTable = ['table', 'advanced-table'].includes(component.type);

      if (isChart || isTable) {
        const { minWidth: _minWidth, ...stylesWithoutMinWidth } = styles;
        styles = stylesWithoutMinWidth;
      }
    }

    // For buttons, remove style properties that should come from properties instead
    // This prevents conflicts where style.border would override properties.border
    if (component.type === ComponentType.BUTTON) {
      const {
        border: _border,
        borderWidth: _borderWidth,
        borderColor: _borderColor,
        borderStyle: _borderStyle,
        borderRadius: _borderRadius,
        boxShadow: _boxShadow,
        backgroundColor: _backgroundColor,
        ...stylesWithoutButtonConflicts
      } = styles;
      styles = stylesWithoutButtonConflicts;
    }

    // Phase 1: For flex-containers, remove background/backgroundColor from wrapper
    // These are applied to the inner FlexContainer component to avoid duplicate backgrounds
    // The FlexContainer.tsx handles its own background/gradient styling
    // Phase 2: Also remove border properties - they're applied to the inner FlexContainer
    // Phase 3: Also remove boxShadow - applied to inner FlexContainer to avoid double shadows
    // Phase 4: Also remove minHeight - flex containers should size based on children
    if (component.type === ComponentType.FLEX_CONTAINER ||
        component.type === ComponentType.CONTAINER ||
        (component.type as string) === 'flex-container') {
      const {
        background: _background,
        backgroundColor: _backgroundColor,
        border: _border,
        borderWidth: _borderWidth,
        borderColor: _borderColor,
        borderStyle: _borderStyle,
        borderRadius: _borderRadius,
        borderTop: _borderTop,
        borderRight: _borderRight,
        borderBottom: _borderBottom,
        borderLeft: _borderLeft,
        borderTopLeftRadius: _borderTopLeftRadius,
        borderTopRightRadius: _borderTopRightRadius,
        borderBottomLeftRadius: _borderBottomLeftRadius,
        borderBottomRightRadius: _borderBottomRightRadius,
        boxShadow: _boxShadow,
        minHeight: _minHeight,
        padding: _padding,
        paddingTop: _paddingTop,
        paddingRight: _paddingRight,
        paddingBottom: _paddingBottom,
        paddingLeft: _paddingLeft,
        ...stylesWithoutContainerConflicts
      } = styles;
      styles = stylesWithoutContainerConflicts;
    }

    return normalizeShorthandConflicts(styles);
  }, [component, positionStyles, mode, isSelected, customStyle, isFlexChild]);
};

/**
 * Unified event handlers hook
 * Centralizes all event handling logic
 */
const useUnifiedEventHandlers = (
  component: Component,
  mode: InteractionMode,
  onComponentInteraction?: (type: string, data: unknown) => void
) => {
  const { handlers } = useInteractionContext();
  const { selectComponent, toggleComponentSelection, setHoveredComponent } = useEditorStore();

  // Build interaction context for functional mode
  const interactionContext = useMemo((): IxnContext | null => {
    if (mode !== 'functional' || !component.interactions?.length) return null;
    const editorState = useEditorStore.getState();
    const responsiveState = useResponsiveStore.getState();
    const rawViewportWidth =
      responsiveState.getViewportDimensions(responsiveState.activeViewport).width;
    const vw = parsePixelDimension(rawViewportWidth) || 1024;
    const bp: 'mobile' | 'tablet' | 'desktop' = vw < 600 ? 'mobile' : vw < 1024 ? 'tablet' : 'desktop';
    return {
      currentBreakpoint: bp,
      getComponent: (id: string) => {
        const c = editorState.components.find((comp: Component) => comp.id === id);
        if (!c) return null;
        return { properties: (c.properties || {}) as Record<string, unknown>, hidden: !!c.hidden };
      },
      navigateToPage: (pageId: string) => {
        onComponentInteraction?.('navigate-page', { pageId });
      },
      setComponentVisibility: (compId: string, visible: boolean) => {
        onComponentInteraction?.('set-visibility', { componentId: compId, visible });
      },
      updateComponentProperty: (compId: string, property: string, value: unknown) => {
        onComponentInteraction?.('update-property', { componentId: compId, property, value });
      },
      getInputValue: (id: string) => liveInputValues.get(id),
      setInputValue: (id: string, value: unknown) => { liveInputValues.set(id, value); },
      showNotification: (message: string, type: 'success' | 'error' | 'info') => {
        console.log(`[Notification][${type}] ${message}`);
      },
      setComponentData: (compId: string, data: Record<string, unknown>[]) => {
        useComponentDataStore.getState().setData(compId, data);
      },
    };
  }, [mode, component.interactions, onComponentInteraction]);

  const fireInteraction = useCallback(async (triggerType: InteractionTriggerType, eventData?: Record<string, unknown>) => {
    if (mode !== 'functional' || !component.interactions?.length || !interactionContext) return;
    await interactionRuntime.handleTrigger(component.id, triggerType, eventData || {}, component.interactions, interactionContext);
  }, [mode, component.id, component.interactions, interactionContext]);

  // Fire onLoad trigger when component mounts in functional mode
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (mode === 'functional' && !hasLoadedRef.current &&
        component.interactions?.some(i => i.enabled && i.trigger.type === 'onLoad')) {
      hasLoadedRef.current = true;
      fireInteraction('onLoad');
    }
    return () => {
      liveInputValues.delete(component.id);
    };
  }, [mode, component.id, component.interactions, fireInteraction]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Functional mode: fire interaction triggers instead of selection
    if (mode === 'functional') {
      fireInteraction('onClick');

      // Property-based navigation: check this component first, then walk up parent chain.
      // Buttons/Icons handle nav in their own onClick (with stopPropagation) so this
      // only fires for other types. The parent walk handles clicks on children inside
      // a navigable container (e.stopPropagation blocks DOM bubbling).
      let navigated = false;
      if (!component.interactions?.length) {
        const action = component.properties?.action;
        const target = component.properties?.target;
        if (action === 'navigate' && typeof target === 'string' && target && handlers.onNavigate) {
          handlers.onNavigate({ targetPageId: target, parameters: {} });
          navigated = true;
        }
      }

      // Walk up parent chain for inherited navigation
      if (!navigated && handlers.onNavigate) {
        // In functional/viewer mode, editor store is empty — use viewer store's componentMap
        const viewerMap = useViewerStore.getState().componentMap;
        const editorComps = useEditorStore.getState().components;
        const lookupParent = (pid: string): Component | undefined => {
          if (viewerMap?.size) return viewerMap.get(pid);
          return editorComps.find((c: Component) => c.id === pid);
        };
        let parentId: string | undefined = component.parentId;
        while (parentId) {
          const parent = lookupParent(parentId);
          if (!parent) break;
          if (parent.properties?.action === 'navigate' && typeof parent.properties?.target === 'string' && parent.properties.target) {
            handlers.onNavigate({ targetPageId: parent.properties.target, parameters: {} });
            break;
          }
          parentId = parent.parentId;
        }
      }
      return;
    }

    // Allow selection in both editing and viewing modes (AI-first workflow)
    // In viewing mode, users can select components to give AI commands
    if (mode === "editing" || mode === "viewing") {
      // Use Shift+Click for multi-select (standard behavior in most applications)
      const multiSelect = e.shiftKey;

      if (multiSelect) {
        toggleComponentSelection(component.id);
      } else {
        selectComponent(component.id);
        // Only call onSelect handler during single-select to avoid overwriting multi-selection
        handlers.onSelect?.(component.id);
      }
      onComponentInteraction?.("select", { componentId: component.id, multiSelect });
    }
  }, [
    mode,
    component.id,
    component.interactions?.length,
    component.parentId,
    component.properties?.action,
    component.properties?.target,
    selectComponent,
    toggleComponentSelection,
    handlers,
    onComponentInteraction,
    fireInteraction,
  ]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (mode === "editing" && handlers.onEditProperties) {
      handlers.onEditProperties(component.id);
      onComponentInteraction?.("edit-properties", { componentId: component.id });
    }
  }, [mode, component.id, handlers, onComponentInteraction]);

  const handleMouseEnter = useCallback(() => {
    if (mode === 'functional') {
      fireInteraction('onHover');
      return;
    }
    // Allow hover in both editing and viewing modes (for visual feedback)
    if (mode === "editing" || mode === "viewing") {
      setHoveredComponent(component.id);
      onComponentInteraction?.("hover", { componentId: component.id, hovered: true });
    }
  }, [mode, component.id, setHoveredComponent, onComponentInteraction, fireInteraction]);

  const handleMouseLeave = useCallback(() => {
    if (mode === 'functional') {
      fireInteraction('onHoverEnd');
      return;
    }
    // Allow hover in both editing and viewing modes (for visual feedback)
    if (mode === "editing" || mode === "viewing") {
      setHoveredComponent(undefined);
      onComponentInteraction?.("hover", { componentId: component.id, hovered: false });
    }
  }, [mode, component.id, setHoveredComponent, onComponentInteraction, fireInteraction]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode === "editing" && !component.locked) {
      onComponentInteraction?.("drag-start", {
        componentId: component.id,
        startPosition: { x: e.clientX, y: e.clientY },
      });
    }
  }, [mode, component.id, component.locked, onComponentInteraction]);

  return {
    // Enable onClick in editing, viewing, and functional modes
    onClick: mode === "editing" || mode === "viewing" || mode === "functional" ? handleClick : undefined,
    onDoubleClick: mode === "editing" ? handleDoubleClick : undefined,
    onMouseDown: mode === "editing" ? handleMouseDown : undefined,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };
};

// ============================================================================
// Selection Overlay Component
// ============================================================================

// [Resize Removal] showResizeHandles prop removed - AI-first architecture
const SelectionOverlay: React.FC<{
  isSelected: boolean;
  isHovered: boolean;
  isPreviewHighlight?: boolean;
  componentName?: string;
  isLocked?: boolean;
  nestingDepth?: number;
}> = ({ isSelected, isHovered, isPreviewHighlight, componentName, isLocked, nestingDepth = 0 }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  if (!isSelected && !isHovered && !isPreviewHighlight) return null;

  // Preview highlight color (orange/amber for clarification hover)
  // Extended to match NestingColor interface for type safety
  const PREVIEW_HIGHLIGHT_COLOR = {
    ring: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.3)',
    bg: '#fffbeb',
    text: '#78350f',
    label: 'Preview',
    description: 'Component being previewed for clarification',
    tailwindRing: 'ring-amber-500',
    tailwindBg: 'bg-amber-50',
    tailwindText: 'text-amber-900',
  };

  // Determine color based on state and depth
  let colorScheme;
  if (isPreviewHighlight) {
    colorScheme = PREVIEW_HIGHLIGHT_COLOR;
  } else if (isLocked) {
    colorScheme = LOCKED_COLOR;
  } else if (isHovered && !isSelected) {
    colorScheme = HOVER_COLOR;
  } else {
    colorScheme = getColorForDepth(nestingDepth);
  }

  const showDepthWarning = !isLocked && shouldShowDepthWarning(nestingDepth);

  return (
    <>
      {/* Selection/hover/preview border with depth-based color and smooth animation */}
      <div
        className={cn(
          "absolute inset-0 pointer-events-none border-2 rounded",
          "transition-all duration-300 ease-out",
          isSelected && [
            "animate-in fade-in-0 zoom-in-95 duration-200",
            "animate-pulse-subtle" // Subtle pulse effect for selected components
          ],
          isPreviewHighlight && [
            "animate-in fade-in-0 zoom-in-95 duration-150",
          ]
        )}
        style={{
          borderColor: colorScheme.ring,
          zIndex: isPreviewHighlight ? 1001 : 1000,
          animation: isPreviewHighlight
            ? 'preview-pulse 1s ease-in-out infinite'
            : 'none',
        }}
        aria-label={`${isPreviewHighlight ? 'Preview' : isSelected ? 'Selected' : 'Hovered'}: ${componentName} at nesting level ${nestingDepth}`}
        data-testid="selection-indicator"
      />

      {/* Preview highlight label */}
      {isPreviewHighlight && componentName && (
        <div
          className="absolute -top-7 left-0 px-2 py-1 rounded whitespace-nowrap z-50 pointer-events-none shadow-lg animate-in fade-in-0 slide-in-from-bottom-2 duration-150"
          style={{
            backgroundColor: colorScheme.ring,
            color: '#ffffff',
            // Reset inherited text styles from parent component
            WebkitTextStroke: '0',
            textShadow: 'none',
            textDecoration: 'none',
          }}
        >
          <span className="font-semibold text-sm">🎯 {componentName}</span>
        </div>
      )}

      {/* Component label with depth badge - enhanced animations */}
      {isSelected && componentName && (
        <div
          className={cn(
            "absolute -top-7 left-0 px-2 py-1 rounded whitespace-nowrap z-50 pointer-events-auto shadow-lg flex items-center gap-2",
            "animate-in fade-in-0 slide-in-from-bottom-2 duration-300",
            "backdrop-blur-sm"
          )}
          style={{
            backgroundColor: colorScheme.ring,
            color: '#ffffff',
            // Reset inherited text styles from parent component
            WebkitTextStroke: '0',
            textShadow: 'none',
            textDecoration: 'none',
          }}
        >
          <span className="font-semibold text-sm">{componentName}</span>
          {isLocked && <span className="text-yellow-300 animate-pulse">🔒</span>}
          {showDepthWarning && (
            <span
              className="text-yellow-300 text-xs animate-pulse"
              title={`Deep nesting detected (Level ${nestingDepth}). Consider refactoring.`}
            >
              ⚠️
            </span>
          )}

          {/* Nesting depth badge with tooltip */}
          <span
            className={cn(
              "px-1.5 py-0.5 text-xs rounded font-bold cursor-help relative",
              "transition-all duration-200 hover:scale-110",
              "animate-in fade-in-0 zoom-in-95 duration-300 delay-100"
            )}
            style={{
              backgroundColor: colorScheme.bg,
              color: colorScheme.text,
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            title={colorScheme.description}
          >
            {colorScheme.label}

            {/* Tooltip for depth badge */}
            {showTooltip && (
              <div
                className={cn(
                  "absolute top-full left-1/2 -translate-x-1/2 mt-2",
                  "px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-xl",
                  "whitespace-normal w-48 z-[60]",
                  "animate-in fade-in-0 slide-in-from-top-2 duration-200",
                  "pointer-events-none"
                )}
                style={{
                  borderLeft: `3px solid ${colorScheme.ring}`,
                }}
              >
                <div className="font-semibold mb-1">Nesting Level: {nestingDepth}</div>
                <div className="text-gray-300 text-xs leading-relaxed">
                  {colorScheme.description}
                </div>
                {nestingDepth >= 3 && (
                  <div className="mt-2 text-yellow-300 text-xs">
                    💡 Tip: Consider flattening component hierarchy
                  </div>
                )}
              </div>
            )}
          </span>
        </div>
      )}

      {/* Resize handles removed - AI-first workflow doesn't need manual resizing */}
    </>
  );
};

// ============================================================================
// Main ComponentWrapper Component
// ============================================================================

export const ComponentWrapper: React.FC<ComponentWrapperProps> = ({
  component,
  children,
  viewport,
  containerDimensions,
  canvasWidth: _canvasWidth,
  canvasHeight: _canvasHeight,
  isFlexChild = false,
  flexDirection: _flexDirection,
  enableVirtualization = false,
  viewportBounds,
  spatialIndex: _spatialIndex, // Deprecated, ignored
  className,
  style,
  activeColorScheme,
  onComponentInteraction,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { mode } = useInteractionContext();
  const editorStore = useEditorStore();
  // Subscribe to previewHighlightId with a selector to ensure re-renders
  const previewHighlightId = useEditorStore((state) => state.previewHighlightId);
  // Allow selection/hover in both editing and viewing modes (AI-first workflow)
  const isSelected = (mode === "editing" || mode === "viewing") && editorStore.isComponentSelected(component.id);
  const isHovered = (mode === "editing" || mode === "viewing") && editorStore.hoveredComponent === component.id;
  const isPreviewHighlight = (mode === "editing" || mode === "viewing") && previewHighlightId === component.id;

  // Auto-detect responsive breakpoint based on window size
  // PRIORITY: Use viewport prop if provided (from ResponsiveEditorCanvas), fall back to detected breakpoint
  // This ensures editor mobile preview uses the selected viewport, not the actual window size
  const detectedBreakpoint = useResponsiveViewport();
  const activeViewport = (viewport || detectedBreakpoint) as Viewport | ResponsiveBreakpoint | undefined;
  const currentBreakpointName = getBreakpointFromViewport(activeViewport);
  const renderComponent = useMemo(
    () => withNormalizedComponentStyle(component, currentBreakpointName, activeColorScheme),
    [component, currentBreakpointName, activeColorScheme],
  );

  // Get all components for expression evaluation and child lookup
  // Use viewer store in viewer/functional mode, editor store in editing mode
  // Important: viewer componentMap contains ALL components including children
  // (state.components only has root-level, children are in _childComponents)
  const viewerComponentMap = useViewerComponentMap();
  const allComponents = useMemo(() => {
    if (mode === "viewing" || mode === "functional") {
      return Array.from(viewerComponentMap.values());
    }
    return editorStore.components;
  }, [mode, viewerComponentMap, editorStore.components]);

  // Week 3: Component hierarchy is now automatically rebuilt by editor-store
  // Debug logs removed - parentId relationships are fixed on load

  // Convert viewport prop to Viewport enum for SharedComponentRenderer using width-based detection
  const viewportEnum = useMemo<Viewport>(() => {
    const breakpointName = getBreakpointFromViewport(activeViewport);
    return breakpointName === 'mobile' ? Viewport.MOBILE :
           breakpointName === 'tablet' ? Viewport.TABLET :
           Viewport.DESKTOP;
  }, [activeViewport]);

  // Calculate nesting depth for color-coded selection
  const nestingDepth = useMemo(() => {
    return getComponentDepth(renderComponent.id, allComponents);
  }, [renderComponent.id, allComponents]);

  // Calculate unified position
  const positionStyles = useUnifiedPosition(
    renderComponent,
    mode,
    activeViewport,
    containerDimensions,
    isFlexChild
  );

  // Calculate unified styles
  const unifiedStyles = useUnifiedStyles(
    renderComponent,
    positionStyles,
    mode,
    isSelected,
    className,
    style,
    isFlexChild
  );

  // Setup unified event handlers
  const eventHandlers = useUnifiedEventHandlers(
    renderComponent,
    mode,
    onComponentInteraction
  );

  // Check if component should be virtualized (performance optimization)
  const shouldRender = useMemo(() => {
    if (!enableVirtualization || !viewportBounds) return true;

    // Skip virtualization if size is not properly defined or uses relative values
    if (!renderComponent.size) return true;

    const width = typeof renderComponent.size.width === 'number' ? renderComponent.size.width : 0;
    const height = typeof renderComponent.size.height === 'number' ? renderComponent.size.height : 0;

    // Skip virtualization for components with non-numeric dimensions
    if (width === 0 || height === 0) return true;

    // Flex-only architecture: x/y placement is deprecated, default to 0
    const compLeft = 0;
    const compTop = 0;
    const compRight = compLeft + width;
    const compBottom = compTop + height;

    return !(
      compRight < viewportBounds.x ||
      compLeft > viewportBounds.x + viewportBounds.width ||
      compBottom < viewportBounds.y ||
      compTop > viewportBounds.y + viewportBounds.height
    );
  }, [renderComponent, enableVirtualization, viewportBounds]);

  // ========================================================================
  // Tooltip Configuration
  // Extract and validate tooltip config for non-chart/map components
  // Must be defined before any early returns (React hooks rules)
  // ========================================================================
  const tooltipConfig = useMemo(() => {
    // Skip components with native tooltip systems
    if (NATIVE_TOOLTIP_COMPONENTS.has(renderComponent.type)) {
      return null;
    }

    const tooltip = renderComponent.properties?.tooltip as TooltipConfig | undefined;

    // Treat empty/whitespace-only text or disabled as no tooltip
    if (!tooltip?.text?.trim() || tooltip.enabled === false) {
      return null;
    }

    return {
      text: tooltip.text.trim(),
      side: tooltip.side || 'top',
      delayDuration: tooltip.delayDuration ?? 400
    };
  }, [renderComponent.type, renderComponent.properties?.tooltip]);

  // [Resize Removal] Manual resize functionality removed - AI-first architecture
  // All component sizing is now done via AI chat commands (e.g., "width 300", "make it wider")
  // The editor store's resizeComponent method is preserved for programmatic sizing

  // Check if component should render (after all hooks are called)
  if (!shouldRender) return null;

  // Phase 3.1: Check for breakpoint-specific hidden property
  const breakpointHidden = renderComponent.responsivePosition?.breakpoints?.[currentBreakpointName]?.hidden;
  const isHiddenForBreakpoint = breakpointHidden === true;

  // In viewer/functional mode, completely hide components marked as hidden
  // This includes both component.hidden and breakpoint-specific hidden
  // In editing mode, show them with reduced opacity so they can be managed
  if ((renderComponent.hidden || isHiddenForBreakpoint) && (mode === "viewing" || mode === "functional")) {
    return null;
  }

  // [Resize Removal] ResizableComponent wrapper removed
  // Selection and hover states are handled by SelectionOverlay below

  // Default rendering without resize
  // For chart/table/container/text flex children, only apply w-full (not h-full) to allow content-based height
  // EXCEPTION: If alignSelf: stretch is set, we need h-full to pass through height from parent
  // EXCEPTION 2: If width is "auto" and flexGrow is 0, don't apply w-full - respect explicit auto sizing
  const isChart = ['chart', 'bar-chart', 'line-chart', 'pie-chart', 'scatter-chart',
                   'area-chart', 'heatmap-chart', 'treemap-chart', 'gauge-chart', 'vertical-gauge-chart'].includes(renderComponent.type);
  const isTable = ['table', 'advanced-table'].includes(renderComponent.type);
  const isContainer = ['flex-container', 'container', 'card'].includes(renderComponent.type);
  const isText = renderComponent.type === 'text';

  // Check if component explicitly wants intrinsic/auto width (e.g., status badges, pills,
  // breadcrumb items, compact header groups). For flex children, forcing w-full on these
  // components causes row layouts to wrap and collapse into cramped vertical stacks.
  const constraints = renderComponent.responsivePosition?.constraints;
  const breakpointWidth = renderComponent.responsivePosition?.breakpoints?.[currentBreakpointName]?.style?.width
    ?? renderComponent.responsivePosition?.breakpoints?.[currentBreakpointName]?.size?.width;
  const responsiveWidth = renderComponent.responsivePosition?.responsiveSize?.width;
  const hasExplicitAutoWidth =
    isAutoWidthValue(renderComponent.size?.width) ||
    isAutoWidthValue(renderComponent.style?.width) ||
    isAutoWidthValue(breakpointWidth) ||
    isAutoWidthValue(responsiveWidth) ||
    (renderComponent.size?.width === undefined && getResponsiveConstraintValue(constraints, 'flexBasis', currentBreakpointName) === 'auto');

  // Check if alignSelf: stretch is set (means component should stretch to fill parent height)
  const hasAlignSelfStretch = (() => {
    // Check breakpoint-specific constraints based on active viewport
    const breakpoints = renderComponent.responsivePosition?.breakpoints;
    if (breakpoints) {
      // Determine current breakpoint from activeViewport using width-based detection
      const breakpoint = getBreakpointFromViewport(activeViewport);
      const bpConstraints = breakpoints[breakpoint]?.constraints;
      if (bpConstraints?.alignSelf === 'stretch') return true;
    }
    // Check base constraints
    if (getResponsiveConstraintValue(renderComponent.responsivePosition?.constraints, 'alignSelf', currentBreakpointName) === 'stretch') return true;
    return false;
  })();

  // PHASE 5 FIX: Check if component has explicit height constraints from ANY source
  // If so, the ComponentWrapper needs h-full to fill the FlexContainer child wrapper's height
  // Sources: breakpoint style height/maxHeight, responsiveSize height/maxHeight, component.size.height
  // This fixes the "FlexContainer overflow past parent" bug where height chain is broken
  const hasExplicitHeightConstraint = (() => {
    if (componentUsesContentDrivenHeight(renderComponent) || shouldIgnoreSmallFixedHeightOverride(renderComponent)) return false;

    // Check component.size.height (from resize/AI commands)
    if (renderComponent.size?.height !== undefined && renderComponent.size?.height !== null) return true;

    const breakpoints = renderComponent.responsivePosition?.breakpoints;
    if (breakpoints) {
      const breakpoint = getBreakpointFromViewport(activeViewport);
      const bpStyle = breakpoints[breakpoint]?.style;
      // Check breakpoint style height or maxHeight
      if (bpStyle?.height !== undefined) return true;
      if (bpStyle?.maxHeight !== undefined) return true;
    }

    // Check responsiveSize height/maxHeight
    const responsiveSize = renderComponent.responsivePosition?.responsiveSize;
    if (responsiveSize?.height !== undefined) return true;
    if (responsiveSize?.maxHeight !== undefined) return true;

    return false;
  })();

  // When alignSelf: stretch is set OR explicit height constraint is set, we need h-full to pass through height
  const shouldOnlyApplyWidth = (isChart || isTable || isContainer || isText) && !hasAlignSelfStretch && !hasExplicitHeightConstraint;

  // ========================================================================
  // Render Helpers
  // ========================================================================

  /**
   * Wraps component content with Tooltip if configured.
   * Tooltips display in all modes: editing, viewing, and functional.
   */
  const renderContent = () => {
    const content = children || (
      <SharedComponentRenderer
        component={renderComponent}
        allComponents={allComponents}
        viewport={viewportEnum}
        activeColorScheme={activeColorScheme}
      />
    );

    // No tooltip configured - return content as-is
    if (!tooltipConfig) {
      return content;
    }

    // Wrap with Radix Tooltip
    return (
      <Tooltip delayDuration={tooltipConfig.delayDuration}>
        <TooltipTrigger asChild>
          <div
            className="w-full h-full"
            tabIndex={0}
            aria-describedby={`tooltip-${component.id}`}
          >
            {content}
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            id={`tooltip-${component.id}`}
            side={tooltipConfig.side}
            className="max-w-xs break-words z-[9999]"
            role="tooltip"
          >
            {tooltipConfig.text.length > 200
              ? `${tooltipConfig.text.slice(0, 200)}...`
              : tooltipConfig.text}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "component-wrapper",
        "transition-all duration-200",
        // Add full width/height for flex children to ensure proper sizing
        // BUT: For charts/tables/containers WITHOUT alignSelf:stretch OR explicit height constraint, only apply w-full
        // PHASE 5 FIX: When explicit height constraint is set (from any source), apply h-full to maintain height chain
        // EXCEPTION: Components with explicit auto width (width: "auto" + flexGrow: 0) should NOT get w-full
        isFlexChild && !shouldOnlyApplyWidth && !hasExplicitAutoWidth && "w-full h-full",
        isFlexChild && shouldOnlyApplyWidth && !hasExplicitAutoWidth && "w-full",
        className,
        {
          // Mode-specific classes - cursor pointer in both editing and viewing modes
          "cursor-pointer": (mode === "editing" || mode === "viewing") && !component.locked,
          "cursor-not-allowed": component.locked,
          // Phase 3.1: Show reduced opacity for hidden components (including breakpoint-hidden)
          "opacity-30": component.hidden || isHiddenForBreakpoint,

          // Selection classes (handled by SelectionOverlay, these are backup/fallback)
          "ring-2 ring-blue-500 ring-opacity-50": isSelected && mode === "editing",
          "ring-1 ring-blue-300 ring-opacity-30": isHovered && mode === "editing" && !isSelected,
        }
      )}
      style={unifiedStyles}
      {...eventHandlers}
      data-component-wrapper="true"
      data-component-id={component.id}
      data-component-type={component.type}
      data-interaction-mode={mode}
      data-selected={isSelected}
      data-locked={component.locked}
      data-hidden={component.hidden || isHiddenForBreakpoint}
      data-hidden-on-breakpoint={isHiddenForBreakpoint ? currentBreakpointName : undefined}
      data-has-tooltip={!!tooltipConfig}
    >
      {/* Render component content with optional tooltip wrapper */}
      {renderContent()}


      {/* Selection overlay for editing and viewing modes (AI-first workflow) */}
      {/* [Resize Removal] showResizeHandles prop removed - AI-first architecture */}
      {(mode === "editing" || mode === "viewing") && (
        <SelectionOverlay
          isSelected={isSelected}
          isHovered={isHovered}
          isPreviewHighlight={isPreviewHighlight}
          componentName={renderComponent.name}
          isLocked={renderComponent.locked}
          nestingDepth={nestingDepth}
        />
      )}
    </div>
  );
};

// ============================================================================
// Export
// ============================================================================

export default ComponentWrapper;
